import { z } from "zod";
import { completionSourceFingerprintSchema } from "./completionSourceFingerprint";
import { completionDeliveryIdSchema as id } from "./completionDelivery";
import { parseCompletionUndoAction } from "./completionUndoAction";

const revision = z.number().int().nonnegative();
const hash = z.string().regex(/^[a-f0-9]{64}$/);
const scope = { expectedOwnerId: id, expectedCompanyId: id, sceneId: id.nullable(), expectedCatalogRevision: revision };
const cursorSchema = z.object({ ...scope, position: z.string().min(1).max(16384) }).strict().nullable();
export const completionUndoRecoveryRequestSchema = z.object({ ...scope,
  cursor: cursorSchema.default(null),
}).strict().superRefine((value, ctx) => {
  if (value.cursor && (Object.keys(scope) as Array<keyof typeof scope>).some(key => value.cursor![key] !== value[key])) {
    ctx.addIssue({ code: "custom", message: "Undo recovery cursor belongs to another workspace" });
  }
});
export type CompletionUndoRecoveryRequest = z.infer<typeof completionUndoRecoveryRequestSchema>;

/** Only these captured fields can authorize recovery; presentation is separate. */
export function preparedUndoIdentity(value: unknown, ownerId: string, companyId: string) {
  const action = parseCompletionUndoAction(value, ownerId, companyId);
  return { action: action.action, status: action.status, componentId: action.componentId,
    undoRequest: action.undoRequest, undoTarget: action.undoTarget };
}

const receiptSchema = z.object({ previewId: id, status: z.enum(["accepted", "succeeded"]),
  receiptId: z.string().optional(), evidence: z.object({ resultDigest: z.string().optional() }).passthrough().optional(),
}).passthrough();
const entrySchema = z.object({ requestReactId: id, previewId: id, registrationDigest: hash,
  requestRevision: revision, requestFingerprint: hash, updatedDate: z.number().finite(),
  state: z.enum(["ready", "changed", "undone"]), action: z.record(z.string(), z.unknown()), receipt: receiptSchema,
}).strict();
const responseSchema = z.object({ ownerId: id, companyId: id, sceneId: id.nullable(), catalogRevision: revision,
  entries: z.array(entrySchema).max(10), unavailableCount: z.number().int().nonnegative().max(10),
  nextCursor: cursorSchema,
}).strict();

export function parseCompletionUndoRecoveryResponse(input: CompletionUndoRecoveryRequest, value: unknown) {
  const result = responseSchema.parse(value);
  if (result.ownerId !== input.expectedOwnerId || result.companyId !== input.expectedCompanyId
    || result.sceneId !== input.sceneId || result.catalogRevision !== input.expectedCatalogRevision) throw new Error("Undo recovery workspace changed");
  completionUndoRecoveryRequestSchema.parse({ ...input, cursor: result.nextCursor });
  const keys = new Set<string>();
  for (const entry of result.entries) {
    if (entry.action.status !== "pending" && entry.action.status !== "completed") throw new Error("Undo recovery action changed");
    const action = parseCompletionUndoAction({ ...entry.action, status: "pending" }, result.ownerId, result.companyId);
    const key = JSON.stringify([entry.requestReactId, entry.previewId]);
    if (keys.has(key) || action.undoRequest.reactId !== entry.requestReactId || action.undoRequest.previewId !== entry.previewId
      || action.undoTarget.undo!.workspaceSceneId !== result.sceneId || entry.receipt.previewId !== entry.previewId) throw new Error("Undo recovery request changed");
    keys.add(key);
    if (entry.action.status === "pending") {
      if (entry.receipt.status !== "accepted" || entry.action.undoOutcome !== undefined) throw new Error("Undo recovery receipt changed");
    } else {
      const proof = z.object({ jobId: id, sourceFingerprint: completionSourceFingerprintSchema, effectId: id, componentId: id,
        fingerprint: hash, completedAt: z.number().finite().nonnegative(), digest: hash }).strict().parse(entry.action.undoOutcome);
      const source = action.undoTarget.undo!.source;
      if (entry.state !== "undone" || entry.receipt.status !== "succeeded" || proof.jobId !== source.jobId
        || proof.sourceFingerprint !== source.sourceFingerprint || proof.effectId !== source.effectId || proof.componentId !== action.componentId
        || entry.receipt.receiptId !== `receipt_${proof.digest.slice(0, 20)}` || entry.receipt.evidence?.resultDigest !== `sha256:${proof.digest}`) throw new Error("Undo recovery proof changed");
    }
  }
  if (result.nextCursor && result.nextCursor.position === input.cursor?.position) throw new Error("Undo recovery cursor did not advance");
  return result;
}
export type CompletionUndoRecoveryResponse = ReturnType<typeof parseCompletionUndoRecoveryResponse>;
